import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { DatePicker } from '@/components/form/inputs/date-picker';
import {
  type Workstream,
  WorkstreamSchema,
} from '@/project-management/entities/workstream';
import { EditWorkstreamMutation } from '@/project-management/mutations/edit-workstream.mutation';

type EditWorkstreamFormProps = {
  workstream: Pick<
    Workstream,
    'id' | 'name' | 'description' | 'startDate' | 'endDate'
  >;
  onClose: () => void;
};

const FormSchema = WorkstreamSchema.pick({
  name: true,
  description: true,
  startDate: true,
  endDate: true,
}).refine(
  (data) => {
    if (!data.startDate || !data.endDate) return true;
    return data.startDate <= data.endDate;
  },
  {
    message: 'End date must be after start date',
    path: ['endDate'],
  },
);
type Form = z.infer<typeof FormSchema>;

export function EditWorkstreamForm({
  workstream,
  onClose,
}: EditWorkstreamFormProps) {
  const editWorkstream = EditWorkstreamMutation.useMutation();
  const form = useForm<Form>({
    defaultValues: workstream,
    resolver: zodResolver(FormSchema),
  });

  async function handleSubmit(data: Form) {
    await toast.promise(
      editWorkstream.mutateAsync({ id: workstream.id, update: data }),
      {
        pending: 'Saving workstream...',
        success: 'Workstream updated successfully.',
        error: 'Failed to update workstream. Please try again.',
      },
    );
    onClose();
  }

  return (
    <>
      <Form {...form}>
        <form
          id="edit-workstream-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Site Preparation, Installation, Testing"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe the specific activities, deliverables, and key milestones for this workstream..."
                    {...field}
                    rows={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid items-start gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <FormControl>
                    <DatePicker value={field.value} onSelect={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date</FormLabel>
                  <FormControl>
                    <DatePicker value={field.value} onSelect={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="edit-workstream-form"
          disabled={editWorkstream.isLoading}
        >
          {editWorkstream.isLoading ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Updating...
            </>
          ) : (
            <>Update Workstream</>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
